import { Head, useForm } from "@inertiajs/react"
import {
  Box,
  Button,
  Callout,
  Card,
  Container,
  Flex,
  Grid,
  Heading,
  Section,
  Select,
  Separator,
  Table,
  Text,
  TextArea,
  TextField,
} from "@radix-ui/themes"
import { AlertCircle, CheckCircle2, UserPlus, Users } from "lucide-react"
import { AdminOnboardingProps } from "@/types/admin_onboarding"

export default function AdminOnboardingNew({
  tracks,
  inputs,
  preview,
  result,
  errors,
}: AdminOnboardingProps) {
  const { data, setData, post, processing } = useForm({
    track_id: inputs?.track_id ? String(inputs.track_id) : "",
    date: inputs?.date || "",
    name: inputs?.name || "",
    handles: inputs?.handles || "",
  })

  const submitPreview = (e: React.FormEvent) => {
    e.preventDefault()
    post("/admin/onboarding/preview", {
      preserveState: true,
      preserveScroll: true,
    })
  }

  const submitCreate = () => {
    post("/admin/onboarding", {
      preserveScroll: true,
    })
  }

  const canPreview = data.track_id && data.date && data.handles.trim().length > 0

  return (
    <div className="flex">
      <Head title="Onboard a Track - Admin" />
      <div className="grow">
        <Section>
          <Container size="3">
            <Heading size="7" mb="2">
              Onboard a Track
            </Heading>
            <Text size="3" as="p" color="gray" mb="6">
              Backfill a track's history. Pick the track, enter a past event date,
              and paste the Instagram handles of the media groups that attended.
              Handles that already exist are reused; new ones become guest profiles
              that auto-link when their owner signs up.
            </Text>

            {errors && errors.length > 0 && (
              <Callout.Root color="red" role="alert" mb="4">
                <Callout.Icon>
                  <AlertCircle />
                </Callout.Icon>
                <Callout.Text>{errors.join(", ")}</Callout.Text>
              </Callout.Root>
            )}

            <Card>
              <Box p="2">
                <form onSubmit={submitPreview}>
                  <Grid columns="1" gap="5">
                    <Box>
                      <Flex gap="2" direction="column">
                        <Text size="3" weight="medium">
                          Track *
                        </Text>
                        <Select.Root
                          value={data.track_id}
                          onValueChange={(value) => setData("track_id", value)}
                        >
                          <Select.Trigger placeholder="Select a track…" />
                          <Select.Content>
                            {tracks.map((track) => (
                              <Select.Item key={track.id} value={String(track.id)}>
                                {track.name}
                              </Select.Item>
                            ))}
                          </Select.Content>
                        </Select.Root>
                      </Flex>
                    </Box>

                    <Grid columns="2" gap="5">
                      <Box>
                        <Flex gap="2" direction="column">
                          <Text size="3" weight="medium">
                            Event Date *
                          </Text>
                          <TextField.Root
                            size="3"
                            type="date"
                            value={data.date}
                            onChange={(e) => setData("date", e.target.value)}
                            required
                          />
                        </Flex>
                      </Box>

                      <Box>
                        <Flex gap="2" direction="column">
                          <Text size="3" weight="medium">
                            Event Name
                          </Text>
                          <TextField.Root
                            size="3"
                            value={data.name}
                            onChange={(e) => setData("name", e.target.value)}
                            placeholder="Optional"
                          />
                        </Flex>
                      </Box>
                    </Grid>

                    <Box>
                      <Flex gap="2" direction="column">
                        <Text size="3" weight="medium">
                          Instagram Handles *
                        </Text>
                        <Text size="2" color="gray">
                          One per line, or comma-separated. "@" and full URLs are fine.
                        </Text>
                        <TextArea
                          size="3"
                          value={data.handles}
                          onChange={(e) => setData("handles", e.target.value)}
                          placeholder={"@shootersmedia\nhttps://instagram.com/anotherone\nthirdhandle"}
                          style={{ minHeight: "160px" }}
                        />
                      </Flex>
                    </Box>

                    <Flex gap="3" justify="end">
                      <Button
                        size="3"
                        type="submit"
                        variant="soft"
                        disabled={processing || !canPreview}
                      >
                        Preview
                      </Button>
                    </Flex>
                  </Grid>
                </form>
              </Box>
            </Card>

            {preview && preview.length > 0 && !result && (
              <Box mt="6">
                <Heading size="5" mb="3">
                  Preview
                </Heading>
                <Table.Root variant="surface">
                  <Table.Header>
                    <Table.Row>
                      <Table.ColumnHeaderCell>Handle</Table.ColumnHeaderCell>
                      <Table.ColumnHeaderCell>Result</Table.ColumnHeaderCell>
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    {preview.map((row) => (
                      <Table.Row key={row.normalized}>
                        <Table.Cell>@{row.normalized}</Table.Cell>
                        <Table.Cell>
                          {row.status === "existing" ? (
                            <Flex align="center" gap="2">
                              <Users size={16} />
                              <Text>Matches {row.participant_name}</Text>
                            </Flex>
                          ) : (
                            <Flex align="center" gap="2" style={{ color: "var(--accent-11)" }}>
                              <UserPlus size={16} />
                              <Text>New guest profile</Text>
                            </Flex>
                          )}
                        </Table.Cell>
                      </Table.Row>
                    ))}
                  </Table.Body>
                </Table.Root>

                <Separator my="4" size="4" />

                <Flex gap="3" justify="end">
                  <Button size="3" onClick={submitCreate} disabled={processing}>
                    Confirm &amp; Create
                  </Button>
                </Flex>
              </Box>
            )}

            {result && (
              <Box mt="6">
                <Callout.Root color="green" mb="4">
                  <Callout.Icon>
                    <CheckCircle2 />
                  </Callout.Icon>
                  <Callout.Text>
                    Backfilled {result.event_name || "event"} at {result.track_name}.
                  </Callout.Text>
                </Callout.Root>
                <Card>
                  <Box p="2">
                    <Grid columns="2" gap="3">
                      <Text size="2" color="gray">New guest profiles created</Text>
                      <Text size="2" weight="medium">{result.participants_created}</Text>
                      <Text size="2" color="gray">Existing profiles reused</Text>
                      <Text size="2" weight="medium">{result.participants_reused}</Text>
                      <Text size="2" color="gray">Attendance records added</Text>
                      <Text size="2" weight="medium">{result.applications_created}</Text>
                      {result.applications_existing > 0 && (
                        <>
                          <Text size="2" color="gray">Attendance already on file</Text>
                          <Text size="2" weight="medium">{result.applications_existing}</Text>
                        </>
                      )}
                    </Grid>
                  </Box>
                </Card>
              </Box>
            )}
          </Container>
        </Section>
      </div>
    </div>
  )
}
